import type { Color, PDFDocument as PDFDocumentType, PDFPage } from "pdf-lib";

export type InventoryLabelItem = {
  sku: string;
  brand: string;
  color: string;
  publicPrice: number;
  copies: number;
};

export type LabelCalibration = {
  horizontalMm: number;
  verticalMm: number;
  horizontalPitchMm: number;
  verticalPitchMm: number;
};

export const officeDepotLetterProfile = Object.freeze({
  pageWidthMm: 215.9,
  pageHeightMm: 279.4,
  columns: 4,
  rows: 20,
  labelWidthMm: 45,
  labelHeightMm: 13,
  columnGapMm: 5,
  rowGapMm: 0,
  marginTopMm: 11,
  marginBottomMm: 8.4,
  marginLeftMm: 9,
  marginRightMm: 11.9,
  labelsPerPage: 80,
});

const mm = (value: number) => value * 72 / 25.4;

function printable(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function download(bytes: Uint8Array, filename: string) {
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const code39: Record<string, string> = {
  "0": "nnwwnwnnw", "1": "wnnwnnnnw", "2": "nnwwnnnnw", "3": "wnwwnnnnn",
  "4": "nnnwwnnnw", "5": "wnnwwnnnn", "6": "nnwwwnnnn", "7": "nnnwnnwnw",
  "8": "wnnwnnwnn", "9": "nnwwnnwnn", "*": "nwnnwnwnn",
};

function drawBarcode(page: PDFPage, sku: string, x: number, y: number, width: number, height: number, black: Color) {
  const encoded = `*${sku.replace(/\D/g, "")}*`;
  const units = [...encoded].reduce((total, character) => {
    const pattern = code39[character] || code39["*"]!;
    return total + [...pattern].reduce((sum, unit) => sum + (unit === "w" ? 2.4 : 1), 0) + 1;
  }, 0);
  const unitWidth = width / units;
  let cursor = x;
  for (const character of encoded) {
    const pattern = code39[character] || code39["*"]!;
    [...pattern].forEach((unit, index) => {
      const segment = unitWidth * (unit === "w" ? 2.4 : 1);
      if (index % 2 === 0) page.drawRectangle({ x: cursor, y, width: segment, height, color: black });
      cursor += segment;
    });
    cursor += unitWidth;
  }
}

export function labelPositionMm(index: number, calibration: LabelCalibration) {
  const profile = officeDepotLetterProfile;
  const column = index % profile.columns;
  const row = Math.floor(index / profile.columns);
  const horizontalPitch = profile.labelWidthMm + profile.columnGapMm + calibration.horizontalPitchMm;
  const verticalPitch = profile.labelHeightMm + profile.rowGapMm + calibration.verticalPitchMm;
  return {
    x: profile.marginLeftMm + column * horizontalPitch + calibration.horizontalMm,
    top: profile.marginTopMm + row * verticalPitch + calibration.verticalMm,
  };
}

function labelPosition(index: number, calibration: LabelCalibration) {
  const position = labelPositionMm(index, calibration);
  return {
    x: mm(position.x),
    y: mm(officeDepotLetterProfile.pageHeightMm - position.top - officeDepotLetterProfile.labelHeightMm),
  };
}

async function newDocument(PDFDocument: typeof import("pdf-lib")["PDFDocument"]): Promise<PDFDocumentType> {
  const documentPdf = await PDFDocument.create();
  documentPdf.setTitle("Etiquetas de inventario · COSMO ÓPTICAS");
  documentPdf.setSubject("Etiquetas descargables en formato carta");
  documentPdf.setCreator("COSMO ÓPTICAS");
  return documentPdf;
}

export async function createInventoryLabelsPdf(items: InventoryLabelItem[], calibration: LabelCalibration) {
  const expanded = items.flatMap((item) => Array.from({ length: item.copies }, () => item));
  if (!expanded.length) throw new Error("No hay etiquetas para generar.");
  const { PDFDocument, StandardFonts, grayscale, rgb } = await import("pdf-lib");
  const documentPdf = await newDocument(PDFDocument);
  const regular = await documentPdf.embedFont(StandardFonts.Helvetica);
  const bold = await documentPdf.embedFont(StandardFonts.HelveticaBold);
  const profile = officeDepotLetterProfile;
  const labelWidth = mm(profile.labelWidthMm);
  const labelHeight = mm(profile.labelHeightMm);
  for (let pageIndex = 0; pageIndex < Math.ceil(expanded.length / profile.labelsPerPage); pageIndex += 1) {
    const page = documentPdf.addPage([mm(profile.pageWidthMm), mm(profile.pageHeightMm)]);
    expanded.slice(pageIndex * profile.labelsPerPage, (pageIndex + 1) * profile.labelsPerPage)
      .forEach((item, localIndex) => {
        const { x, y } = labelPosition(localIndex, calibration);
        const sku = printable(item.sku).slice(0, 12);
        const price = `$${Math.round(item.publicPrice).toLocaleString("en-US")}`;
        const priceWidth = bold.widthOfTextAtSize(price, 5.2);
        page.drawText(sku, { x: x + mm(0.7), y: y + labelHeight - mm(2.2), size: 5.2, font: bold, color: rgb(0.02, 0.16, 0.27) });
        page.drawText(price, { x: x + labelWidth - priceWidth - mm(0.7), y: y + labelHeight - mm(2.2), size: 5.2, font: bold, color: rgb(0.02, 0.16, 0.27) });
        const description = printable(`${item.brand} ${item.color}`).slice(0, 30);
        page.drawText(description, { x: x + mm(0.7), y: y + mm(6), size: 4.1, font: regular, color: grayscale(0.25) });
        drawBarcode(page, sku, x + mm(0.8), y + mm(1), labelWidth - mm(1.6), mm(3), grayscale(0));
      });
  }
  const bytes = await documentPdf.save();
  return { bytes, count: expanded.length };
}

export async function downloadInventoryLabels(items: InventoryLabelItem[], calibration: LabelCalibration) {
  const { bytes, count } = await createInventoryLabelsPdf(items, calibration);
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  download(bytes, `etiquetas_inventario_${date}.pdf`);
  return count;
}

export async function createLabelCalibrationPdf(calibration: LabelCalibration) {
  const { PDFDocument, StandardFonts, grayscale, rgb } = await import("pdf-lib");
  const documentPdf = await newDocument(PDFDocument);
  const regular = await documentPdf.embedFont(StandardFonts.Helvetica);
  const profile = officeDepotLetterProfile;
  const page = documentPdf.addPage([mm(profile.pageWidthMm), mm(profile.pageHeightMm)]);
  const width = mm(profile.labelWidthMm);
  const height = mm(profile.labelHeightMm);
  for (let index = 0; index < profile.labelsPerPage; index += 1) {
    const { x, y } = labelPosition(index, calibration);
    page.drawRectangle({ x, y, width, height, borderColor: rgb(0.02, 0.49, 0.52), borderWidth: 0.35 });
    page.drawText(String(index + 1), { x: x + 2, y: y + height / 2 - 2, size: 4.5, font: regular, color: grayscale(0.35) });
  }
  return documentPdf.save();
}

export async function downloadLabelCalibration(calibration: LabelCalibration) {
  const bytes = await createLabelCalibrationPdf(calibration);
  download(bytes, "calibracion_etiquetas_carta_80.pdf");
}
